import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'
import { formatPlanDate, formatUsd, formatQuarterLabel } from './installmentFormat'
import PaymentMethodBadge from '../../../components/PaymentMethodBadge'
import PaymentHistory from './components/PaymentHistory'

const STATUS_CLASS = {
  active: 'bg-sky-50 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300 border-sky-200 dark:border-sky-800',
  completed: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
  cancelled: 'bg-gray-50 dark:bg-slate-700/50 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-600'
}

export default function PaymentInstallmentStatusPage () {
  const { applicationId, planId } = useParams()
  const navigate = useNavigate()
  const [plan, setPlan] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const load = async () => {
      const res = await fetch(`${apiURL}/api/applications/${applicationId}/payment-installments/${planId}`, {
        headers: { Authorization: account.accessToken }
      })
      const data = await res.json()
      if (data.response?.error === 'Failed To Authenticate Token.') {
        const refreshed = await refreshAccessToken()
        if (refreshed) return load()
        return navigate('/', { state: { expired: true } })
      }
      if (data.response?.error) {
        setError(data.response.error)
        return
      }
      setPlan(data.response)
    }
    load()
  }, [applicationId, planId, navigate])

  if (error) {
    return <div className='p-8 text-rose-600'>{error}</div>
  }

  if (!plan) {
    return <div className='p-8 text-slate-500'>Loading payment plan…</div>
  }

  const statusKey = plan.status === 'completed' ? 'completed' : plan.status === 'cancelled' ? 'cancelled' : 'active'
  const statusLabel = statusKey === 'completed' ? 'Complete' : statusKey === 'cancelled' ? 'Cancelled' : 'Active'
  const original = Number(plan.originalAmount || 0)
  const remaining = Number(plan.remainingBalance || 0)
  const collected = Math.max(0, original - remaining)
  const progress = original > 0 ? Math.min(100, Math.round((collected / original) * 100)) : 0
  const nextCharge = plan.nextChargeDate
    ? `${formatUsd(plan.nextChargeAmount)} on ${formatPlanDate(plan.nextChargeDate)}`
    : 'All payments complete'

  return (
    <div className='w-full px-4 sm:px-6 lg:px-8 py-8'>
      <button
        type='button'
        onClick={() => navigate(`/applications/${applicationId}/quarter-breakdown`)}
        className='flex items-center gap-2 text-sm font-semibold text-slate-500 min-h-11 mb-6'
      >
        <ArrowLeft size={16} /> Quarter breakdown
      </button>

      <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-slate-900 dark:text-white'>Payment plan</h1>
          <p className='text-slate-600 dark:text-slate-300 mt-1'>Invoice #{plan.invoiceNumber || '—'} · {formatQuarterLabel(plan.quarter)}</p>
        </div>
        <span className={`inline-flex items-center self-start px-3 py-1.5 rounded-full text-xs font-semibold border ${STATUS_CLASS[statusKey]}`}>
          {statusLabel}
        </span>
      </div>

      <div className='rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-5'>
        <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3'>
          <div>
            <p className='text-xs font-semibold uppercase tracking-widest text-slate-400'>Paid so far</p>
            <p className='text-xl font-bold text-slate-900 dark:text-white tabular-nums'>
              {formatUsd(collected)} <span className='text-sm font-medium text-slate-500'>of {formatUsd(original)}</span>
            </p>
          </div>
          <p className='text-sm text-slate-500'>{progress}% paid · {plan.installmentCount} {plan.frequency} payments</p>
        </div>
        <div
          className='h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden'
          role='progressbar'
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label='Installment collection progress'
        >
          <div className='h-full rounded-full bg-ertc-primary transition-all duration-200' style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className='mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
        <Meta label='Remaining' value={formatUsd(remaining)} />
        <Meta label='Next payment' value={nextCharge} />
        <div className='rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-4'>
          <p className='text-xs font-semibold uppercase tracking-widest text-slate-400'>Payment method</p>
          <div className='mt-2'>
            <PaymentMethodBadge methodType={plan.methodType} methodLabel={plan.methodLabel} last4={plan.last4} />
          </div>
        </div>
      </div>

      <section className='mt-8'>
        <h2 className='text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-4'>Payment history</h2>
        <PaymentHistory schedule={plan.originalSchedule} charges={plan.charges} />
      </section>
    </div>
  )
}

function Meta ({ label, value }) {
  return (
    <div className='rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-4'>
      <p className='text-xs font-semibold uppercase tracking-widest text-slate-400'>{label}</p>
      <p className='mt-1 text-sm font-semibold text-slate-900 dark:text-white'>{value}</p>
    </div>
  )
}
